import type {KeyTextField, LinkField} from '@prismicio/types';
import pick from 'lodash/pick';
import {linkResolver} from '../helpers/link-resolver';
import type {LocationSuggestion, QuerySuggestion} from '../types/custom';
import {useTypeGuards} from './useTypeGuards';

type GetSearchURLParams = {
    baseURLLink: LinkField | KeyTextField;
    query: string;
    location: LocationSuggestion | null;
    sourceComponent?: string; // to track the search_started Amplitude event
};

const QUERY_SUGGEST_URL = '/suggest/search';
const QUERY_SUGGEST_URL_V2 = '/api/search/autocomplete';

const {isFilledKeyText, isFilledLink, isQuerySuggestionsResponse, isQuerySuggestionsV2Response} = useTypeGuards();

const LOCATION_FIELDS = [
    'administrativeAreaLevel1',
    'administrativeAreaLevel1Code',
    'administrativeAreaLevel2',
    'administrativeAreaLevel2Code',
    'administrativeAreaLevel3',
    'administrativeAreaLevel3Code?',
    'administrativeAreaLevel4',
    'administrativeAreaLevel4Code',
    'city',
    'country',
    'countryCode',
    'formattedAddress',
    'region',
    'street',
    'street2',
    'zipCode',
];

function locationAsRecord(location: LocationSuggestion | null): Record<string, string> | null {
    if (!location) {
        return null;
    }

    const locationRecord = pick(location, LOCATION_FIELDS) as Record<string, string>;

    if (location.loc) {
        locationRecord.lat = location.loc.lat.toString();
        locationRecord.lon = location.loc.lon.toString();
    }

    if (location.remoteAllowed) {
        locationRecord.remoteAllowed = location.remoteAllowed.toString();
    }

    if (location.remoteEuropa) {
        locationRecord.remoteEuropa = location.remoteEuropa.toString();
    }

    return locationRecord;
}

function recordAsLocation(locationRecord: Record<string, string>): LocationSuggestion | null {
    if (!locationRecord.location) {
        return null;
    }

    const location: LocationSuggestion = pick(locationRecord, LOCATION_FIELDS);
    const lat = Number(locationRecord.lat);
    const lon = Number(locationRecord.lon);

    if (lat && Number.isFinite(lat) && lon && Number.isFinite(lon)) {
        location.loc = {
            lat,
            lon,
        };
    }

    if (locationRecord.remoteAllowed) {
        location.remoteAllowed = Boolean(locationRecord.remoteAllowed);
    }

    if (locationRecord.remoteEuropa) {
        location.remoteEuropa = Boolean(locationRecord.remoteEuropa);
    }

    return location;
}

function getSearchURL({baseURLLink, query, location, sourceComponent}: GetSearchURLParams): string {
    if (baseURLLink === null || (!isFilledKeyText(baseURLLink) && !isFilledLink(baseURLLink))) {
        return window.location.href;
    }
    const baseURL = isFilledKeyText(baseURLLink) ? baseURLLink : baseURLLink.link_type === 'Document' ? linkResolver(baseURLLink) : baseURLLink.url;

    const searchParams = new URLSearchParams();
    searchParams.set('q', query);

    if (location) {
        searchParams.set('as', 't');
        searchParams.set('location', location.formattedAddress || '');

        Object.keys(location).forEach((key) => {
            const value: LocationSuggestion[keyof LocationSuggestion] | undefined = location[<keyof LocationSuggestion>key];
            if (value != null) {
                searchParams.set(key, value.toString());
            }
        });
    }

    if (sourceComponent) {
        searchParams.set('sourceComponent', sourceComponent);
    }

    return `${baseURL}${baseURL.includes('?') ? '&' : '?'}${searchParams.toString()}`;
}

async function getQueryAutocomplete(input: string, autocompleteApiV2 = false): Promise<QuerySuggestion[] | string[]> {
    const searchParams = new URLSearchParams();
    const QUERY_MAX_LENGTH = 100;
    const sanitizedInput = input.trim().slice(0, QUERY_MAX_LENGTH);
    searchParams.set('q', sanitizedInput);

    try {
        const endpoint = autocompleteApiV2 ? QUERY_SUGGEST_URL_V2 : QUERY_SUGGEST_URL;
        const response = await fetch(`${endpoint}?${searchParams.toString()}`);

        const data = await response.json();
        if (isQuerySuggestionsResponse(data) || isQuerySuggestionsV2Response(data)) {
            return data.suggestions;
        }
    } catch (e) {
        // eslint-disable-next-line
        console.error(e);
    }

    return [];
}

export function useSearchHelpers() {
    return {
        locationAsRecord,
        recordAsLocation,
        getSearchURL,
        getQueryAutocomplete,
    };
}
